import { z } from "zod";

const shipperAndBillToSchema = z.object({
  id: z.number(),
  name: z.string(),
  refClientCode: z.string().nullable(),
  address: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  contactEmail: z.string().nullable(),
});

const consigneeSchema = shipperAndBillToSchema.extend({
  website: z.string().nullish(),
  lat: z.number().nullish(),
  lng: z.number().nullish(),
});

const deliveryHoursSchema = z.object({
  startUtc: z.date(),
  endUtc: z.date(),
});

const dimensionsSchema = z.object({
  handlingUnits: z.number().nullable(),
  seqNumber: z.number().nullable(),
  length: z.number().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
});

export type Dimensions = z.infer<typeof dimensionsSchema>;

const itemsSummarySchema = z.object({
  pallets: z.number(),
  pieces: z.number(),
  handlingUnits: z.number(),
  weight: z.number(),
  dimensions: z.array(dimensionsSchema).nullish(),
});

const descriptionOfArticlesSchema = z.object({
  seqNumber: z.number(),
  saidToContain: z.string().nullable(),
  description: z.string(),
  commodityCode: z.string(),
  pieces: z.number(),
  weight: z.number(),
});

const proNotesSchema = z.object({
  id: z.number().nullish(),
  timestamp: z.date(),
  csrLoginId: z.string().nullable(),
  terminalCode: z.string().nullable(),
  content: z.string().nullable(),
});

export type ProNote = z.infer<typeof proNotesSchema>;

export const createProNoteSchema = z.object({
  timestamp: z.date(),
  csrLoginId: z.string().nullable(),
  terminalCode: z.string().nullable(),
  content: z.string().nullable(),
});

export type CreateProNoteRequest = z.infer<typeof createProNoteSchema>;

export const editProNoteSchema = createProNoteSchema.extend({ id: z.number() });

export type EditProNoteRequest = z.infer<typeof editProNoteSchema>;

const drNotesSchema = z.object({
  id: z.number().nullish(),
  timestamp: z.date(),
  recordingParty: z.string().nullable(),
  terminalCode: z.string().nullable(),
  content: z.string().nullable(),
});

export type DrNote = z.infer<typeof drNotesSchema>;

export const createDrNoteSchema = z.object({
  shipmentId: z.number(),
  content: z.string(),
});

export type CreateDrNoteRequest = z.infer<typeof createDrNoteSchema>;

export const editDrNoteSchema = createDrNoteSchema.extend({ id: z.number() });

export type EditDrNoteRequest = z.infer<typeof editDrNoteSchema>;

export const appointmentRequiredShipmentDetailSchema = z.object({
  consignee: consigneeSchema.nullish(),
  shipper: shipperAndBillToSchema.nullish(),
  billTo: shipperAndBillToSchema.nullish(),
  deliveryHours: deliveryHoursSchema,
  chargeType: z.string(),
  createdByLoginId: z.string().nullable(),
  collectAmount: z.number().nullable(),
  purchaseOrder: z.string().nullable(),
  shipDate: z.date(),
  itemsSummary: itemsSummarySchema,
  descriptionOfArticles: z.array(descriptionOfArticlesSchema),
  doh: z.number(),
  proNotes: z.array(proNotesSchema),
  drNotes: z.array(drNotesSchema),
});

export type AppointmentRequiredShipmentDetail = z.infer<
  typeof appointmentRequiredShipmentDetailSchema
>;

const previousDeliveriesItemSchema = z.object({
  shipmentId: z.number(),
  consigneeId: z.number(),
  consigneeName: z.string(),
  proNumber: z.string(),
  city: z.string(),
  state: z.string(),
  deliveryDateUtc: z.date(),
});

export const previousDeliveriesSchema = z.array(previousDeliveriesItemSchema);

export type PreviousDeliveries = z.infer<typeof previousDeliveriesItemSchema>;

const outcomeSchema = z.enum([
  "NO_ANSWER",
  "LEFT_VM",
  "REFUSED",
  "WAIT_EMAIL",
  "OTHER",
]);

export type Outcome = z.infer<typeof outcomeSchema>;

const callAttemptSchema = z.object({
  id: z.number(),
  outcome: outcomeSchema,
  notes: z.string().nullish(),
  otherReason: z.string().nullish(),
  attemptedBy: z.string().nullish(),
  attemptedOn: z.date(),
});

export const callAttemptsSchema = z.array(callAttemptSchema);

export type CallAttempt = z.infer<typeof callAttemptSchema>;

export const createCallAttemptSchema = z.object({
  outcome: outcomeSchema,
  notes: z.string().nullish(),
  otherReason: z.string().nullish(),
});

export type CreateCallAttemptRequest = z.infer<typeof createCallAttemptSchema>;

export const shipmentBolSchema = z.object({
  // eslint-disable-next-line camelcase
  bol_available: z.boolean(),
  // eslint-disable-next-line camelcase
  bol_url: z.string().nullable(),
});

export type ShipmentBol = z.infer<typeof shipmentBolSchema>;
const associatedShipmentSchema = z.object({
  shipmentId: z.number(),
  proNumber: z.string(),
  serviceDueDate: z.coerce.date().nullable(),
  pallets: z.number(),
  weight: z.number(),
  doh: z.number(),
  tripNumber: z.string().nullable(),
  appointmentDate: z.coerce.date().nullable(),
});

export const associatedShipmentsSchema = z.array(associatedShipmentSchema);

export type AssociatedShipment = z.infer<typeof associatedShipmentSchema>;
